<template>
  <el-dialog
    :model-value="modelValue"
    :title="t('payment.title')"
    width="520px"
    class="payment-dialog"
    @close="handleClose"
  >
    <el-form label-position="top" class="payment-form">
      <el-form-item :label="t('payment.rechargeCount')" class="recharge-count-row">
        <el-input-number
          v-model="rechargeCount"
          :min="1"
          :step="1"
          :precision="0"
          class="count-input"
          @change="handleRechargeCountChange"
        />
      </el-form-item>
<div class="total-row">
        <span>{{ t('payment.remainingCount') }}</span>
        <span>{{ store.state.userInfo.cishu }}</span>
      </div>

      <div class="billing-row">
        <span class="billing-label">{{ t('payment.billingStandard') }}</span>
        <!-- <span class="original-price">{{ formatPrice(originalUnitPrice) }}/{{ t('payment.perUse') }}</span> -->
         <!-- {{ t('payment.limitedTimeDiscount') }} -->
           <!-- 限时折扣 -->
        <span class="discount-price">{{ formatPrice(discountUnitPrice) }}/{{ t('payment.perUse') }}</span>
      </div>

      <div class="total-row">
        <span>{{ t('payment.total') }}</span>
        <strong>{{ formatPrice(totalPrice) }}</strong>
      </div>

      <el-radio-group v-model="paymentMethod" class="payment-methods" @change="handlePaymentMethodChange">
        <el-radio-button value="wechat">
          <span class="payment-method-label">
            <img src="@/assets/wx.png" alt="">
            <span>{{ t('payment.wechatPay') }}</span>
          </span>
        </el-radio-button>
        <el-radio-button value="alipay">
          <span class="payment-method-label">
            <img src="@/assets/zfb.png" alt="">
            <span>{{ t('payment.alipay') }}</span>
          </span>
        </el-radio-button>
      </el-radio-group>

      <div v-if="paymentMethod === 'wechat'" class="qr-section">
        <img v-if="displayQrCodeUrl" :src="displayQrCodeUrl" :alt="t('payment.qrCodeAlt')" class="qr-code">
        <div v-else class="qr-code qr-code-placeholder" :aria-label="t('payment.qrCodePlaceholder')">
          <img alt="">
        </div>
        <p>{{ qrCodeLoading ? t('payment.qrCodeLoading') : t('payment.scanTip') }}</p>
      </div>
      <div v-else class="alipay-section">
        <img src="@/assets/zfb.png" alt="">
        <p>{{ alipayLoading ? t('payment.alipayOpening') : t('payment.alipayRedirectTip') }}</p>
      </div>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleNotCompleted">{{ t('payment.notCompleted') }}</el-button>
        <el-button type="primary" @click="handleCompleted">{{ t('payment.completed') }}</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import QRCode from 'qrcode'
import rawAxios from 'axios'
import store from '@/store'
import { ElMessage } from 'element-plus'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  // 原始单价
  originalUnitPrice: {
    type: Number,
    default: 30
  },
  // 折扣单价
  discountUnitPrice: {
    type: Number,
    default: 80
  },
  qrCodeUrl: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'completed', 'not-completed'])

const { t } = useI18n()
const rechargeCount = ref(5)
const paymentMethod = ref('wechat')
const generatedQrCodeUrl = ref('')
const qrCodeLoading = ref(false)
const alipayLoading = ref(false)
const paymentOrderId = ref(null)
let qrCodeRequestId = 0
let wechatQrCodeTimer = null
let alipayPaymentTimer = null
let pendingAlipayWindow = null

const paymentService = rawAxios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  timeout: 1000000
})

paymentService.interceptors.request.use((config) => {
  if (store.state.token) {
    config.headers.Authorization = store.state.token
  }
  config.headers['Accept-Language'] = localStorage.getItem('locale') || 'zh-CN'
  return config
})

paymentService.interceptors.response.use((res) => {
  const data = res.data
  if (data.code === 0) {
    return data
  }
  return Promise.reject(data)
})

const totalPrice = computed(() => rechargeCount.value * props.discountUnitPrice)
const displayQrCodeUrl = computed(() => props.qrCodeUrl || generatedQrCodeUrl.value)

watch(rechargeCount, (value) => {
  const normalizedValue = Math.max(1, Math.floor(Number(value) || 1))
  if (value !== normalizedValue) {
    rechargeCount.value = normalizedValue
  }
})

watch(
  [() => props.modelValue, rechargeCount],
  ([visible, count]) => {
    if (!visible) {
      clearWechatQrCodeTimer()
      clearAlipayPaymentTimer()
      closePendingAlipayWindow()
      qrCodeRequestId += 1
      generatedQrCodeUrl.value = ''
      paymentOrderId.value = null
      qrCodeLoading.value = false
      alipayLoading.value = false
      return
    }
    if (paymentMethod.value === 'wechat') {
      scheduleWechatQrCodeLoad(count)
    }
  }
)

const formatPrice = (value) => {
  return `¥${Number(value).toFixed(2)}`
}

const clearWechatQrCodeTimer = () => {
  if (wechatQrCodeTimer) {
    clearTimeout(wechatQrCodeTimer)
    wechatQrCodeTimer = null
  }
}

const clearAlipayPaymentTimer = () => {
  if (alipayPaymentTimer) {
    clearTimeout(alipayPaymentTimer)
    alipayPaymentTimer = null
  }
}

const closePendingAlipayWindow = () => {
  if (pendingAlipayWindow && !pendingAlipayWindow.closed) {
    pendingAlipayWindow.close()
  }
  pendingAlipayWindow = null
}

// 延迟生成微信支付二维码，并清除上一笔订单信息。
const scheduleWechatQrCodeLoad = (count) => {
  clearWechatQrCodeTimer()
  qrCodeLoading.value = true
  generatedQrCodeUrl.value = ''
  paymentOrderId.value = null
  wechatQrCodeTimer = setTimeout(() => {
    wechatQrCodeTimer = null
    loadWechatQrCode(count)
  }, 1000)
}

// 请求微信支付信息，保存订单号并生成二维码。
const loadWechatQrCode = async (count) => {
  if (!store.state.userId) {
    generatedQrCodeUrl.value = ''
    qrCodeLoading.value = false
    return
  }

  const requestId = ++qrCodeRequestId
  qrCodeLoading.value = true
  generatedQrCodeUrl.value = ''

  try {
    const res = await paymentService.get('/api/wxpay/', {
      params: {
        user_id: store.state.userId,
        cishu: count
      }
    })
    if (requestId !== qrCodeRequestId) return
    paymentOrderId.value = res.order_id ?? null
    generatedQrCodeUrl.value = await QRCode.toDataURL(res.url, {
      width: 360,
      margin: 1
    })
  } catch (error) {
    console.error('Failed to generate payment QR code', error)
    if (requestId === qrCodeRequestId) {
      generatedQrCodeUrl.value = ''
      paymentOrderId.value = null
    }
  } finally {
    if (requestId === qrCodeRequestId) {
      qrCodeLoading.value = false
    }
  }
}

const prepareAlipayPaymentWindow = () => {
  if (pendingAlipayWindow && !pendingAlipayWindow.closed) {
    return pendingAlipayWindow
  }

  const paymentWindow = window.open('', '_blank')
  if (!paymentWindow) {
    ElMessage.warning(t('payment.allowPopup'))
    return null
  }

  paymentWindow.opener = null
  pendingAlipayWindow = paymentWindow
  return paymentWindow
}

// 延迟打开支付宝支付页面，并清除上一笔订单信息。
const scheduleAlipayPaymentPage = (count) => {
  clearWechatQrCodeTimer()
  clearAlipayPaymentTimer()
  closePendingAlipayWindow()
  qrCodeRequestId += 1
  generatedQrCodeUrl.value = ''
  paymentOrderId.value = null
  qrCodeLoading.value = false
  alipayLoading.value = true

  alipayPaymentTimer = setTimeout(() => {
    alipayPaymentTimer = null
    openAlipayPaymentPage(count)
  }, 1000)
}

// 请求支付宝支付地址；接口返回订单号时同步保存。
const openAlipayPaymentPage = async (count) => {
  if (!store.state.userId) {
    closePendingAlipayWindow()
    alipayLoading.value = false
    return
  }

  clearWechatQrCodeTimer()
  clearAlipayPaymentTimer()
  let paymentWindow = pendingAlipayWindow && !pendingAlipayWindow.closed ? pendingAlipayWindow : null
  if (!paymentWindow) {
    paymentWindow = prepareAlipayPaymentWindow()
  }
  if (!paymentWindow) return

  const requestId = ++qrCodeRequestId
  generatedQrCodeUrl.value = ''
  qrCodeLoading.value = false
  alipayLoading.value = true

  try {
    const res = await paymentService.get('/api/alipay/', {
      params: {
        user_id: store.state.userId,
        cishu: count
      }
    })
    if (requestId !== qrCodeRequestId) {
      if (pendingAlipayWindow !== paymentWindow) {
        paymentWindow.close()
      }
      return
    }
    paymentOrderId.value = res.order_id ?? null
    paymentWindow.location.href = res.url
    if (pendingAlipayWindow === paymentWindow) {
      pendingAlipayWindow = null
    }
  } catch (error) {
    paymentWindow.close()
    if (pendingAlipayWindow === paymentWindow) {
      pendingAlipayWindow = null
    }
    console.error('Failed to open Alipay payment page', error)
    ElMessage.error(t('payment.alipayOpenFailed'))
  } finally {
    if (requestId === qrCodeRequestId) {
      alipayLoading.value = false
    }
  }
}

// 切换支付方式时清理旧订单，并加载当前支付方式的信息。
const handlePaymentMethodChange = (method) => {
  clearWechatQrCodeTimer()
  clearAlipayPaymentTimer()
  closePendingAlipayWindow()
  generatedQrCodeUrl.value = ''
  paymentOrderId.value = null
  qrCodeLoading.value = false
  alipayLoading.value = false
  if (!props.modelValue) return

  if (method === 'alipay') {
    openAlipayPaymentPage(rechargeCount.value)
  } else {
    scheduleWechatQrCodeLoad(rechargeCount.value)
  }
}

const handleRechargeCountChange = (value) => {
  const normalizedValue = Math.max(1, Math.floor(Number(value) || 1))
  if (value !== normalizedValue) {
    rechargeCount.value = normalizedValue
  }
  if (!props.modelValue || paymentMethod.value !== 'alipay') return

  scheduleAlipayPaymentPage(normalizedValue)
}

// 关闭支付弹窗并清理本次支付产生的临时状态。
const closeDialog = () => {
  clearWechatQrCodeTimer()
  clearAlipayPaymentTimer()
  closePendingAlipayWindow()
  qrCodeRequestId += 1
  generatedQrCodeUrl.value = ''
  paymentOrderId.value = null
  qrCodeLoading.value = false
  alipayLoading.value = false
  paymentMethod.value = 'wechat'
  emit('update:modelValue', false)
}

const handleClose = () => {
  closeDialog()
}

// 关闭弹窗后查询订单状态，再将支付结果通知父组件。
const handleCompleted = async () => {
  const paymentResult = {
    count: rechargeCount.value,
    paymentMethod: paymentMethod.value,
    totalPrice: totalPrice.value,
    orderId: paymentOrderId.value,
    userId: store.state.userId
  }
  closeDialog()

  if (!paymentResult.orderId) {
    emit('completed', paymentResult)
    return
  }

  try {
    const res = await paymentService.get('/api/order/', {
      params: {
        order_id: paymentResult.orderId,
        user_id: paymentResult.userId
      }
    })
    emit('completed', {
      ...paymentResult,
      zhuangtai: res.zhuangtai === true
    })
  } catch (error) {
    console.error('Failed to query payment status', error)
    ElMessage.error(t('payment.orderQueryFailed'))
  }
}

const handleNotCompleted = () => {
  emit('not-completed')
  closeDialog()
}
</script>

<style scoped lang="scss">
.payment-form {
  padding: 0 12px;

  .recharge-count-row {
    display: flex;
    align-items: center;
  }

  .count-input {
    width: 100%;
  }
}

.billing-row,
.total-row {
  display: flex;
  align-items: center;
  min-height: 44px;
}

.billing-row {
  color: #606266;

  .billing-label {
    margin-right: auto;
  }

  .original-price {
    color: #909399;
    margin-right: 10px;
    text-decoration: line-through;
  }

  .discount-price {
    color: #f56c6c;
    font-size: 18px;
    font-weight: 600;
  }
}

.total-row {
  border-top: 1px solid #ebeef5;
  justify-content: space-between;
  margin: 8px 0 18px;
  padding-top: 10px;

  strong {
    color: #f56c6c;
    font-size: 28px;
  }
}

.payment-methods {
  display: flex;
  width: 100%;

  :deep(.el-radio-button) {
    flex: 1;
  }

  :deep(.el-radio-button__inner) {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  :deep(.el-radio-button.is-active .el-radio-button__inner) {
    color: #409eff !important;
    background-color: #ffffff !important;
    border-color: #409eff !important;
    box-shadow: none !important;
    outline: 1px solid #409eff;
    outline-offset: -1px;
  }

  .payment-method-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    img {
      width: 22px;
      height: 22px;
      object-fit: contain;
    }
  }
}

.qr-section {
  margin-top: 22px;
  text-align: center;

  .qr-code {
    display: block;
    width: 180px;
    height: 180px;
    margin: 0 auto;
    object-fit: contain;
  }

  .qr-code-placeholder {
    background-color: #f5f7fa;
    border: 1px dashed #c0c4cc;

    img {
      opacity: 0;
    }
  }

  p {
    color: #909399;
    font-size: 14px;
    margin: 10px 0 0;
  }
}

.alipay-section {
  min-height: 212px;
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  img {
    width: 56px;
    height: 56px;
    object-fit: contain;
  }

  p {
    max-width: 360px;
    margin: 16px 0 0;
    color: #606266;
    font-size: 14px;
    line-height: 1.6;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 560px) {
  .qr-section .qr-code {
    width: 150px;
    height: 150px;
  }
}
</style>
